import React from 'react';
import PropTypes from 'prop-types';
import { css } from 'emotion';
import styled, { keyframes } from 'react-emotion';
import Helmet from 'react-helmet';
import Img from 'gatsby-image';
import Link from 'gatsby-link';
import kebabCase from 'lodash/kebabCase';
import Tags from '../components/Tags';
import TagsTeam from '../components/TagsTeam';
import Suggestions from '../components/Suggestions';
import SEO from '../components/SEO';
import Container from '../components/Container';
import Content from '../components/Content';
import WaveNews from '../components/WaveNews';
import Button from '../components/Button';
import Footer from '../components/Footer';
import Line from '../components/Line';
import LinePad from '../components/LinePad';

import Images from '../icons/fa-solid/Images';

import { hideS } from '../util/hide';
import config from '../../config/website';

import TextPlated from '../components/TextPlated';

// import FadeIn from "../components/ani/FadeIn";
import SlideUp from '../components/ani/SlideUp';
import Browser from '../util/Browser';

import '../util/prism-okaida.css';

import History from '../icons/fa-solid/History';
import CenterGrid from '../components/CenterGrid';
import SubNav from '../components/SubNav';

import Patreon from '../icons/logo/Patreon';

import FillBg from '../components/FillBg';

import FxVignette from '../components/FxVignette';
import FxTint from '../components/FxTint';

import ReactDisqusComments from 'react-disqus-comments';
import DisqusContainer from '../components/DisqusContainer';

const FooterDiv = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.pure.bright};
  h1 {
    color: ${props => props.theme.colors.pure.bright};
    text-shadow: ${props => props.theme.set.light.shadow._3dThin};
  }
`;

const IconSpan = styled.span`
  top: 0.25rem;
  position: relative;
`;

const PixDiv = styled.div`
  top: 0.35rem;
  position: relative;
`;

const ImagesIconSpan = styled.span`
  top: -0.35rem;
  left: 0.25rem;
  position: relative;
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    animation-timing-function: ease-in;
    transform-origin: 16% 84%;;
    translate(1.25remm, 0.9375 );
  }
  25% {
    animation-timing-function: ease-out;
    transform: scale(1.05);
    transform-origin: 16% 84%;;
    translate(1.25remm, 0.9375 );
  }
  50% {
    transform: scale(1.125);
    animation-timing-function: ease-in;
    transform-origin: 16% 84%;;
    translate(1.25remm, 0.9375 );
  }
  to {
    transform: scale(1);
    animation-timing-function: ease-out;
    transform-origin: 16% 84%;;
    translate(1.25remm, 0.9375 );
  }
`;

const Wrapper = styled.div`
  height: 600px;
  position: relative;
  overflow: hidden;
  .gatsby-image-wrapper {
    z-index: 0;
    height: 600px;
    img {
      animation: ${pulse} 30s infinite;
      animation-delay: 1400ms;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.m}) {
    height: 500px;
    .gatsby-image-wrapper {
      height: 500px;
    }
  }
  @media (max-width: ${props => props.theme.breakpoints.s}) {
    height: 400px;
    .gatsby-image-wrapper {
      height: 400px;
    }
  }
`;

const Hero = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99;
  color: ${props => props.theme.colors.pure.bright};
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${props => props.theme.layout.base};
  padding: 0 2rem;
  text-align: center;
  h2 {
    color: ${props => props.theme.colors.pure.bright};
    text-shadow: ${props => props.theme.set.light.shadow._3d};
  }
`;

const TitleH2 = styled.h2`
  font-size: 2.95rem;
  color: ${props => props.theme.colors.pure.bright};
  text-shadow: ${props => props.theme.set.light.shadow._3d};
`;

const Information = styled.div`
  font-size: 1.26rem;
  margin-top: 2rem;
  font-family: ${props => props.theme.fontFamily.heading};
  text-shadow: 1px 0px 0 ${props => props.theme.colors.cool.ink}, 0px 1px 0 ${props => props.theme.colors.cool.ink},
    1px 1px 0 ${props => props.theme.colors.cool.ink};
  a {
    color: ${props => props.theme.colors.warm.dark};
    transition: all 0.4s;
    &:hover {
      color: ${props => props.theme.colors.warm.base};
    }
    &:focus {
      color: white;
    }
  }
`;

const TeamsStyle = styled.div`
  font-size: 1.26rem;
  margin-top: -1rem;
  margin-bottom: -0.5rem;
  font-family: ${props => props.theme.fontFamily.heading};
  text-shadow: 1px 0px 0 ${props => props.theme.colors.cool.ink}, 0px 1px 0 ${props => props.theme.colors.cool.ink},
    1px 1px 0 ${props => props.theme.colors.cool.ink};
  a {
    color: ${props => props.theme.colors.fire.light};
    transition: all 0.4s;
    &:hover {
      color: ${props => props.theme.colors.warm.light};
    }
    &:focus {
      color: white;
    }
  }
`;

const ButtonStyle = styled.span`
  svg {
    position: relative;
    top: -0.2rem;
    max-height: 2rem;
    max-width: 2rem;
    g {
      stroke: ${props => props.theme.set.light.placeholder};
      fill: ${props => props.theme.colors.cool.dim0};
      stroke-width: 18;
    }
  }
`;
// stroke-width: 18;
const fontBold = css`
  font-weight: 700;
`;
const Shortcut = styled.div`
  position: relative;
  z-index: 0;
  top: -5.25rem;
  height: 1px;
  @media (max-width: 350px) {
    top: -5.25rem;
  }
`;

let teamsLink = '';
let teamsURL = '';
let SlideUp00 = styled.div``;

const Post = ({ pathContext: { slug, left, right }, data: { markdownRemark: postNode }, location }) => {
  const post = postNode.frontmatter;
  const { sizes } = post.cover.childImageSharp;
  if (!post.id) {
    post.id = slug;
  }
  Img.sizes = sizes;

  if (typeof post.teams[1] !== 'undefined') {
    teamsLink = `${post.teams[0]}+`;
    if (typeof window !== 'undefined') {
      teamsURL = window.location.hostname;
    }

    teamsURL = `${location.pathname}/#teams`;
    if (teamsURL.match(/\/\/#/g)) {
      teamsURL = teamsURL.replace(/\/\/#/g, '/#');
    }
  } else {
    teamsLink = post.teams[0];
    teamsURL = `/teams/${kebabCase(teamsLink)}`;
  }
  // ---------------------------------------------------------------
  // Disqus
  // ---------------------------------------------------------------
  const disqus = {
    shortname: 'sondro',
    url: `${location.hostname}${post.title.text}`,
    identifier: location.pathname,
    title: post.title.text,
    language: 'en',
  };
  // ---------------------------------------------------------------
  if (Browser() === 'chrome') {
    SlideUp00 = SlideUp;
  }
  return (
    <React.Fragment>
      <Helmet title={`${post.title} | ${config.siteTitle}`} />
      <SEO postPath={slug} postNode={postNode} postSEO />
      <Wrapper>
        <Hero>
          <SlideUp00 delay={300} fromY="0px" fromX="32px">
            <TitleH2>{post.title}</TitleH2>
          </SlideUp00>
          <SlideUp00 delay={880} fromY="0px" fromX="-22px">
            <TeamsStyle>
              <Link to={teamsURL}>{`@${teamsLink}`}</Link>
            </TeamsStyle>
          </SlideUp00>
          <SlideUp00 delay={1300} fromY="8px" fromX="0px">
            <Information>
              {post.date} | {postNode.timeToRead}m Read | <span className={hideS} />
              <Link to={`/topics/${kebabCase(post.topic)}`}>{`^${post.topic}`}</Link>
            </Information>
          </SlideUp00>
        </Hero>
        <FxVignette />
        <FxTint />
        <WaveNews />
        <Img sizes={sizes} />
      </Wrapper>
      <Container>
        <FillBg>
          <SlideUp00 delay={250} fromY="0px">
            <SubNav>
              <PixDiv>
                <Link to="/pix">
                  <ImagesIconSpan>
                    <Images />
                  </ImagesIconSpan>
                  <span>{`${'\u00A0'}:${'\u00A0'}PIX`}</span>
                </Link>
              </PixDiv>
            </SubNav>
          </SlideUp00>
          <SlideUp00 delay={425} fromY="12px">
            <Content input={postNode.html} />
            <Shortcut name="teams" />
            <DisqusContainer type="article">
              <Line />
              <TagsTeam tags={post.teams} type="teams" />
              <Tags tags={post.tags} />
              <div style={{ padding: '0.25rem' }} />
              <p>
                <span className={fontBold}>Like this post?</span> Read more from the{' '}
                <TextPlated>
                  <Link to={`/topics/${kebabCase(post.topic)}`}>{`^${post.topic}`}</Link>
                </TextPlated>{' '}
                topic.
              </p>
              <ReactDisqusComments
                shortname={disqus.shortname}
                identifier={disqus.identifier}
                // url={disqus.url}
                title={disqus.title}
                language={disqus.language}
              />
            </DisqusContainer>
            <SlideUp00 fromY="0px" fromX="90px" delay={1400}>
              <LinePad />
            </SlideUp00>
            <br />
            <CenterGrid>
              <SubNav>
                <PixDiv>
                  <Link to="/pix">
                    <ImagesIconSpan>
                      <Images />
                    </ImagesIconSpan>
                    <span>{`${'\u00A0'}:${'\u00A0'}PIX`}</span>
                  </Link>
                </PixDiv>
                <span>{`${'\u00A0'}${'\u00A0'}${'\u00A0'}${'\u00A0'}${'\u00A0'}${'\u00A0'}`}</span>
                <Link to="/news">
                  <IconSpan>
                    <History />
                  </IconSpan>
                  <span>{`${'\u00A0'}:${'\u00A0'}NEWS`}</span>
                </Link>
              </SubNav>
            </CenterGrid>
            <Container>
              <Suggestions left={left} right={right} />
              <div style={{ padding: '2.6823rem' }} />
            </Container>
          </SlideUp00>
        </FillBg>
      </Container>
      <Footer>
        <FooterDiv>
          <h1>Get more insights!</h1>
          <a href="https://www.patreon.com/Sondro" target="_blank" rel="noopener noreferrer">
            <ButtonStyle>
              <Button type="alt">
                <Patreon />
                {'\u00A0'}:{'\u00A0'}:{'\u00A0'}Patreon{'\u00A0'}:{'\u00A0'}:
              </Button>
            </ButtonStyle>
          </a>
        </FooterDiv>
      </Footer>
    </React.Fragment>
  );
};

export default Post;

Post.propTypes = {
  pathContext: PropTypes.shape({
    slug: PropTypes.string.isRequired,
  }),
  data: PropTypes.shape({
    markdownRemark: PropTypes.object.isRequired,
  }),
};

/* eslint no-undef: "off" */
export const pageQuery = graphql`
  query NewsPostBySlug($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      html
      timeToRead
      frontmatter {
        title
        date(formatString: "MMM.DD.YYYY", locale: "en")
        topic
        tags
        teams
        cover {
          childImageSharp {
            sizes(
              maxWidth: 1920
              duotone: { highlight: "#808080", shadow: "#303030", opacity: 93 }
              quality: 90
              traceSVG: { color: "#787878" }
            ) {
              ...GatsbyImageSharpSizes_withWebp_tracedSVG
            }
            resize(width: 1200) {
              src
              height
              width
            }
          }
        }
      }
      excerpt(pruneLength: 180)
      fields {
        slug
      }
    }
  }
`;


// WEBPACK FOOTER //
// ./src/templates/post.jsx